import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toSteward, formatLastLogin, computeStewardStats } from '../utils/stewardAdapter';
import { useUpdateSteward } from '../hooks/useUpdateSteward';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  UserIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  TableCellsIcon,
  UsersIcon,
  ClockIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import { Switch } from '@mui/material';
import {
  getStewards,
  createSteward,
  updateSteward,
  updateStewardPassword,
  deleteSteward,
  getStewardAssignments,
  updateStewardAssignments,
  getTables,
  getVendorStewards,
  getVendorTablesForAssignment,
  assignVendorTablesToSteward
} from '../api/api';
import api from '../api/api';
import { useConfirm } from '../context/ConfirmContext';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import AppIconButton from '../components/ui/AppIconButton';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Typography,
  Divider,
  Button,
  Tab,
  Tabs,
  Card,
  CardContent,
  Avatar,
  Badge,
  CircularProgress
} from '@mui/material';

interface Steward {
  id: number | string;
  name: string;
  email?: string;
  stewardCode?: string;
  active: boolean;
  assignedCount: number;
  lastLogin?: string | null;
  createdAt?: string;
  activeRequests?: number;
  /** Backend flag — when true, this steward is restricted to Income/Expense/EOD entry-only. */
  financeOnlyRestricted?: boolean;
}

interface TableAssignment {
  id: number;
  name: string;
  section: string;
  floor: string;
  isAssigned: boolean;
}

const StewardManagement: React.FC = () => {
  console.info('STEWARD v3 — React Query page loaded');
  const { theme } = useTheme();
  const confirm = useConfirm();
  const { isVendor, isAdmin } = useAuth();
  const queryClient = useQueryClient();

  // State management
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [editingSteward, setEditingSteward] = useState<Steward | null>(null);
  const [selectedSteward, setSelectedSteward] = useState<Steward | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    stewardCode: '',
    password: '',
    confirmPassword: '',
    isActive: true,
    vendorId: '', // For admin to select which vendor
    /** Restrict steward to Income / Expense / EOD add-only access. */
    financeOnlyRestricted: false,
  });

  // Vendors list for admin
  const [vendors, setVendors] = useState<Array<{ id: number; businessName: string; username: string }>>([]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Table assignments
  const [tables, setTables] = useState<TableAssignment[]>([]);
  const [selectedTables, setSelectedTables] = useState<number[]>([]);

  // Error and success states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Tab state for modal
  const [modalTab, setModalTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // React Query for fetching stewards
  const filtersKey = JSON.stringify({ isVendor, isAdmin });
  const { data: stewards = [], isLoading: loading, error: queryError } = useQuery({
    queryKey: ['stewards', filtersKey],
    queryFn: async () => {
      const data = isVendor
        ? await getVendorStewards()
        : await getStewards();
      return Array.isArray(data) ? data.map(toSteward) : [];
    },
    retry: false,
  });

  // Use the new update steward hook
  const updateStewardMutation = useUpdateSteward(filtersKey);

  // Handle toggle errors
  useEffect(() => {
    if (updateStewardMutation.error) {
      const error = updateStewardMutation.error as any;
      const data = error?.response?.data;
      const details = data?.errors ? Object.values(data.errors).flat().join('\n') :
        data?.message || error?.message || 'Failed to save steward';
      setError(details);
    }
  }, [updateStewardMutation.error]);

  // Set error from query if it exists
  useEffect(() => {
    if (queryError) {
      const error = queryError as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to fetch stewards');
    }
  }, [queryError]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['stewards'] });
    setRefreshing(false);
    setSuccess('Data refreshed successfully');
  };

  const fetchVendors = async () => {
    if (!isAdmin) return; // Only admins need to fetch vendors

    setLoadingVendors(true);
    try {
      const params = new URLSearchParams({
        page: '0',
        size: '100', // Get first 100 vendors
        role: 'VENDOR',
        sortBy: 'createdAt',
        sortDirection: 'DESC',
      });
      const response = await api.get(`/admin/users?${params.toString()}`);

      // Transform response to get vendor list
      const vendorList = (response.data.users || []).map((user: any) => ({
        id: user.id,
        username: user.username,
        businessName: user.businessName || user.name || user.username,
      }));

      setVendors(vendorList);

      if (vendorList.length === 0) {
        setError('No vendors found in the system. Please create a vendor first.');
      }
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
      setError('Failed to load vendors list');
    } finally {
      setLoadingVendors(false);
    }
  };

  const handleOpenModal = (steward?: Steward) => {
    if (steward) {
      setEditingSteward(steward);
      setFormData({
        name: steward.name,
        email: steward.email || '',
        stewardCode: steward.stewardCode || '',
        password: '',
        confirmPassword: '',
        isActive: steward.active,
        vendorId: '',
        financeOnlyRestricted: Boolean((steward as Steward & { financeOnlyRestricted?: boolean }).financeOnlyRestricted),
      });
    } else {
      setEditingSteward(null);
      setFormData({
        name: '',
        email: '',
        stewardCode: generateStewardCode(),
        password: '',
        confirmPassword: '',
        isActive: true,
        vendorId: '',
        financeOnlyRestricted: false,
      });

      // Fetch vendors list for admin when creating new steward
      if (isAdmin) {
        fetchVendors();
      }
    }
    setModalTab(0);
    setIsModalOpen(true);
  };

  const generateStewardCode = () => {
    const prefix = 'STW';
    const random = Math.floor(1000 + Math.random() * 9000);
    return `${prefix}${random}`;
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSteward(null);
    setError(null);
    setFieldErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
    } else if (formData.name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Steward code validation
    if (!formData.stewardCode.trim()) {
      errors.stewardCode = 'Steward code is required';
    } else if (formData.stewardCode.length < 3) {
      errors.stewardCode = 'Steward code must be at least 3 characters';
    }

    // Vendor validation (only for admins creating new stewards)
    if (isAdmin && !editingSteward && !formData.vendorId) {
      errors.vendorId = 'Please select a vendor';
    }

    // Password validation (only for new stewards)
    if (!editingSteward) {
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }

      if (!formData.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    } else {
      // For editing, only validate if password is provided
      if (formData.password && formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }

      if (formData.password && formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setFieldErrors(errors);

    // Scroll to first error field
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementsByName(firstErrorField)[0];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // Client-side validation
    if (!validateForm()) {
      return;
    }

    try {
      const stewardData: any = {
        name: formData.name,
        email: formData.email,
        stewardCode: formData.stewardCode,
        isActive: formData.isActive,
        password: formData.password // Always include password for new stewards
      };

      // Add vendorId for admin users creating new stewards
      if (isAdmin && !editingSteward) {
        if (formData.vendorId) {
          stewardData.vendorId = Number(formData.vendorId);
        } else {
          setError('Please select a vendor');
          return;
        }
      }

      // Debug logging
      console.log('🔍 Creating steward with data:', {
        ...stewardData,
        password: '***' // Hide password in logs
      });
      console.log('📋 isAdmin:', isAdmin);
      console.log('📋 editingSteward:', editingSteward);
      console.log('📋 formData.vendorId:', formData.vendorId);
      console.log('📋 stewardData.vendorId:', stewardData.vendorId);
      console.log('📋 Full stewardData object:', JSON.stringify(stewardData, null, 2).replace(/"password":\s*"[^"]*"/, '"password": "***"'));

      if (editingSteward) {
        // Update profile information (WITHOUT password)
        const profileData = {
          name: stewardData.name,
          email: stewardData.email,
          stewardCode: stewardData.stewardCode,
          isActive: stewardData.isActive
        };
        await updateSteward(Number(editingSteward.id), profileData);

        // If password was provided, update it separately
        if (formData.password && formData.password.trim() !== '') {
          await updateStewardPassword(Number(editingSteward.id), {
            password: formData.password,
            confirmPassword: formData.confirmPassword
          });
        }

        setSuccess('Steward updated successfully');
      } else {
        console.log('📤 About to call createSteward with:', stewardData);
        console.log('📤 Actual vendorId value:', stewardData.vendorId, typeof stewardData.vendorId);

        // Explicitly construct the payload to ensure vendorId is included
        const payload: any = {
          name: stewardData.name,
          email: stewardData.email,
          stewardCode: stewardData.stewardCode,
          password: stewardData.password,
          isActive: stewardData.isActive,
          financeOnlyRestricted: Boolean(formData.financeOnlyRestricted),
        };

        if (stewardData.vendorId) {
          payload.vendorId = stewardData.vendorId;
        }

        console.log('📤 Final payload:', payload);
        await createSteward(payload);
        setSuccess('Steward created successfully');
      }

      handleCloseModal();
      queryClient.invalidateQueries({ queryKey: ['stewards'] });
    } catch (err: unknown) {
      const error = err as any;

      // Parse backend error
      if (error.response?.status === 400) {
        const data = error.response?.data;
        const errors: Record<string, string> = {};

        // Check for field-specific errors
        if (data?.errors && typeof data.errors === 'object') {
          Object.keys(data.errors).forEach((field) => {
            const fieldErrors = data.errors[field];
            errors[field] = Array.isArray(fieldErrors) ? fieldErrors[0] : fieldErrors;
          });
        }

        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          // Scroll to first error
          const firstErrorField = Object.keys(errors)[0];
          const element = document.getElementsByName(firstErrorField)[0];
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
          }
        } else {
          // Generic 400 error
          setError(data?.message || data?.error || 'Invalid data provided. Please check your inputs.');
        }
      } else if (error.response?.status === 409) {
        setError('A steward with this email or code already exists');
      } else {
        setError(error.response?.data?.error || error.response?.data?.message || 'Failed to save steward');
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDelete = async (steward: Steward) => {
    const result = await confirm({
      title: 'Delete Steward',
      description: `Are you sure you want to delete ${steward.name}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (result) {
      try {
        await deleteSteward(Number(steward.id));
        setSuccess('Steward deleted successfully');
        queryClient.invalidateQueries({ queryKey: ['stewards'] });
      } catch (err: unknown) {
        const error = err as { response?: { data?: { error?: string } } };
        setError(error.response?.data?.error || 'Failed to delete steward');
      }
    }
  };

  const handleAssignTables = async (steward: Steward) => {
    setSelectedSteward(steward);
    setIsAssignmentModalOpen(true);

    try {
      // Use vendor-specific endpoint if user is a vendor, admin endpoint if admin
      if (isVendor) {
        const tablesData = await getVendorTablesForAssignment(Number(steward.id));
        setTables(tablesData);
        // Extract currently assigned table IDs from response
        const assignedTableIds = tablesData
          .filter((t: TableAssignment & { isAssigned?: boolean }) => t.isAssigned)
          .map((t: { id: number }) => t.id);
        setSelectedTables(assignedTableIds);
      } else {
        // Admin flow
        const [allTables, assignments] = await Promise.all([
          getTables(),
          getStewardAssignments(Number(steward.id))
        ]);

        setTables(allTables);
        setSelectedTables(assignments.map((a: { id: number }) => a.id));
      }
    } catch (err) {
      setError('Failed to fetch table assignments');
    }
  };

  const handleSaveAssignments = async () => {
    if (!selectedSteward) return;

    try {
      // Use vendor-specific endpoint if user is a vendor, admin endpoint if admin
      if (isVendor) {
        await assignVendorTablesToSteward(Number(selectedSteward.id), selectedTables);
      } else {
        await updateStewardAssignments(Number(selectedSteward.id), selectedTables);
      }
      setSuccess('Table assignments updated successfully');
      setIsAssignmentModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['stewards'] });
    } catch (err) {
      setError('Failed to update assignments');
    }
  };

  // Filtering and pagination
  const filteredStewards = stewards.filter(steward => {
    const matchesSearch = steward.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (steward.email && steward.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (steward.stewardCode && steward.stewardCode.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = filterStatus === 'all' ||
      (filterStatus === 'active' && steward.active) ||
      (filterStatus === 'inactive' && !steward.active);

    return matchesSearch && matchesFilter;
  });

  const paginatedStewards = filteredStewards.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Stats cards
  const stats: any = computeStewardStats(stewards);



  return (
    <Box sx={{ p: 3, backgroundColor: theme.background, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.text, mb: 1 }}>
          Steward Management
        </Typography>
        <Typography variant="body1" sx={{ color: theme.gray?.[600] }}>
          Manage your restaurant staff and table assignments
        </Typography>
      </Box>

      {/* Stats Cards - Pastel like Admin Orders */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)', border: 'none', height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: 'var(--text-inverse)', fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>{stats.total}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.25, fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>Total Stewards</Typography>
                </Box>
                <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}><UsersIcon className="h-6 w-6" style={{ color: 'var(--text-inverse)' }} /></Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #34d399 0%, #059669 100%)', border: 'none', height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: 'var(--text-inverse)', fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>{stats.active}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.25, fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>Active Staff</Typography>
                </Box>
                <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}><CheckCircleIcon className="h-6 w-6" style={{ color: 'var(--text-inverse)' }} /></Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', border: 'none', height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: 'var(--text-inverse)', fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>{stats.inactive}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.25, fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>Inactive</Typography>
                </Box>
                <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}><XCircleIcon className="h-6 w-6" style={{ color: 'var(--text-inverse)' }} /></Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)', border: 'none', height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: 'var(--text-inverse)', fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>{stats.withTables}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.25, fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>With Tables</Typography>
                </Box>
                <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}><TableCellsIcon className="h-6 w-6" style={{ color: 'var(--text-inverse)' }} /></Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            alignItems: 'center',
            '& .MuiAlert-action': {
              paddingTop: 0,
              paddingBottom: 0,
              alignItems: 'center',
              marginTop: 0,
            },
            '& .MuiAlert-action .MuiIconButton-root': {
              backgroundColor: 'rgba(239, 68, 68, 0.1) !important',
              border: '1px solid rgba(239, 68, 68, 0.2) !important',
              color: `${theme.danger} !important`,
              borderRadius: '8px',
              padding: '4px',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.2) !important',
                borderColor: 'rgba(239, 68, 68, 0.3) !important',
              }
            }
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Table Section */}
      <Paper sx={{
        backgroundColor: theme.components?.card?.background,
        border: `1px solid ${theme.components?.card?.border}`,
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        {/* Table Header */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${theme.components?.card?.border}` }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                placeholder="Search stewards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MagnifyingGlassIcon className="h-5 w-5" style={{ color: theme.gray?.[600] }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
                  displayEmpty
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="inactive">Inactive Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={5} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <AppIconButton
                icon={<ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />}
                tooltip="Refresh"
                variant="primary"
                onClick={handleRefresh}
                disabled={refreshing}
              />

              {/* TEMP: always render in dev */}
              {true && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PlusIcon className="h-5 w-5" />}
                  onClick={() => handleOpenModal()}
                  sx={{
                    textTransform: 'none !important',
                    fontWeight: '600 !important',
                    px: 2.5,
                    py: 1,
                    backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
                    color: 'var(--text-link) !important',
                    borderWidth: '1px !important',
                    borderStyle: 'solid !important',
                    borderColor: 'rgba(96, 165, 250, 0.31) !important',
                    boxShadow: 'none !important',
                    '&:hover': {
                      backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                      borderColor: 'rgba(96, 165, 250, 0.4) !important',
                      boxShadow: 'none !important',
                    }
                  }}
                >
                  Add Steward
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Loading Bar */}
        {(loading || refreshing) && <LinearProgress />}

        {/* Table */}
        <TableContainer sx={{
          width: '100%',
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: { xs: '900px', sm: '1000px' }
          }
        }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  fontWeight: 600,
                  color: theme.text,
                  minWidth: '140px',
                  whiteSpace: 'nowrap'
                }}>Steward</TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  color: theme.text,
                  minWidth: '80px',
                  whiteSpace: 'nowrap'
                }}>Code</TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  color: theme.text,
                  minWidth: '160px',
                  whiteSpace: 'nowrap'
                }}>Email</TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  color: theme.text,
                  minWidth: '80px',
                  whiteSpace: 'nowrap'
                }}>Status</TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  color: theme.text,
                  minWidth: '90px',
                  whiteSpace: 'nowrap'
                }}>Tables</TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  color: theme.text,
                  minWidth: '120px',
                  whiteSpace: 'nowrap'
                }}>Last Login</TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  color: theme.text,
                  minWidth: '120px',
                  whiteSpace: 'nowrap'
                }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !refreshing ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : paginatedStewards.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" sx={{ color: theme.gray?.[600] }}>
                      No stewards found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedStewards.map((steward) => (
                  <TableRow key={steward.id} hover>
                    <TableCell sx={{ minWidth: '140px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                        <Avatar sx={{ backgroundColor: theme.primary + '20', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                          <UserIcon className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: theme.primary }} />
                        </Avatar>
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: theme.text,
                              fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {steward.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.gray?.[600],
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              display: { xs: 'block', sm: 'block' }
                            }}
                          >
                            ID: {steward.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ minWidth: '80px' }}>
                      <Chip
                        label={steward.stewardCode}
                        size="small"
                        icon={<IdentificationIcon className="h-3 w-3" />}
                        sx={{
                          backgroundColor: theme.gray?.[100],
                          color: theme.text,
                          fontWeight: 600,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          height: { xs: '24px', sm: '32px' }
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ color: theme.gray?.[700], minWidth: '160px' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {steward.email}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ minWidth: '120px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Switch
                          checked={!!steward.active}
                          disabled={updateStewardMutation.isPending}
                          onChange={(e) => {
                            updateStewardMutation.mutate({
                              row: steward,
                              nextActive: e.target.checked
                            });
                          }}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.success,
                              '& + .MuiSwitch-track': {
                                backgroundColor: theme.success,
                              },
                            },
                          }}
                        />
                        <Chip
                          label={steward.active ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            backgroundColor: steward.active ? '#d1fae5 !important' : '#fee2e2 !important',
                            color: steward.active ? '#065f46 !important' : '#991b1b !important',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: '22px',
                            borderRadius: '12px',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: steward.active ? '#86efac' : '#fca5a5',
                          }}
                        />
                        {steward.financeOnlyRestricted && (
                          <Chip
                            label="Finance only"
                            size="small"
                            data-testid={`steward-finance-restricted-${steward.id}`}
                            sx={{
                              backgroundColor: '#EDE9E3 !important', /* basil-todo */
                              color: '#47593E !important', /* basil-todo */
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              height: '22px',
                              borderRadius: '12px',
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              borderColor: '#C9D1BE', /* basil-todo */
                            }}
                          />
                        )}
                      </div>
                    </TableCell>

                    <TableCell sx={{ minWidth: '70px' }}>
                      {steward.assignedCount > 0 ? (
                        <Badge
                          badgeContent={steward.assignedCount}
                          color="primary"
                          sx={{ '& .MuiBadge-badge': { backgroundColor: theme.primary } }}
                        >
                          <TableCellsIcon className="h-4 w-4" style={{ color: theme.gray?.[600] }} />
                        </Badge>
                      ) : (
                        <Typography variant="body2" sx={{ color: theme.gray?.[500], fontSize: '0.8rem' }}>
                          None
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ color: theme.gray?.[600], minWidth: '100px' }}>
                      {steward.lastLogin ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ClockIcon className="h-3 w-3" />
                          <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                            {formatLastLogin(steward.lastLogin)}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                          Never
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right" sx={{ minWidth: '120px' }}>
                      <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, justifyContent: 'flex-end' }}>
                        {/* Assign Tables functionality hidden - not implemented yet in frontend */}
                        {/* <AppIconButton
                          icon={<TableCellsIcon className="h-4 w-4" />}
                          tooltip="Assign Tables"
                          variant="ghost"
                          size="small"
                          onClick={() => handleAssignTables(steward)}
                          sx={{
                            color: theme.secondary,
                            backgroundColor: 'transparent',
                            width: { xs: '32px', sm: '40px' },
                            height: { xs: '32px', sm: '40px' },
                            '&:hover': { backgroundColor: 'transparent' },
                          }}
                        /> */}

                        {/* TEMP: always render in dev */}
                        {true && (
                          <>
                            <AppIconButton
                              icon={<PencilSquareIcon className="h-4 w-4" />}
                              tooltip="Edit"
                              variant="ghost"
                              size="small"
                              onClick={() => handleOpenModal(steward)}
                              sx={{
                                color: theme.primary,
                                backgroundColor: 'transparent',
                                width: { xs: '32px', sm: '40px' },
                                height: { xs: '32px', sm: '40px' },
                                '&:hover': { backgroundColor: 'transparent' },
                              }}
                            />

                            <AppIconButton
                              icon={<TrashIcon className="h-4 w-4" />}
                              tooltip="Delete"
                              variant="ghost"
                              size="small"
                              onClick={() => handleDelete(steward)}
                              sx={{
                                color: theme.danger,
                                backgroundColor: 'transparent',
                                width: { xs: '32px', sm: '40px' },
                                height: { xs: '32px', sm: '40px' },
                                '&:hover': { backgroundColor: 'transparent' },
                              }}
                            />
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredStewards.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingSteward ? 'Edit Steward' : 'Add New Steward'}
        maxWidth="md"
      >
        <Box sx={{ mt: 2 }}>
          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                alignItems: 'center',
                '& .MuiAlert-action': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  alignItems: 'center',
                  marginTop: 0,
                },
                '& .MuiAlert-action .MuiIconButton-root': {
                  backgroundColor: 'rgba(239, 68, 68, 0.1) !important',
                  border: '1px solid rgba(239, 68, 68, 0.2) !important',
                  color: `${theme.danger} !important`,
                  borderRadius: '8px',
                  padding: '4px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.2) !important',
                    borderColor: 'rgba(239, 68, 68, 0.3) !important',
                  }
                }
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Tabs
            value={modalTab}
            onChange={(_, v) => setModalTab(v)}
            sx={{
              mb: 3,
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                minHeight: { xs: 44, sm: 48 },
                px: { xs: 2, sm: 3 },
                backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
                color: 'var(--text-link) !important',
                border: '1px solid rgba(96, 165, 250, 0.31) !important',
                transition: 'all 0.2s ease',
                mr: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                  borderColor: 'rgba(96, 165, 250, 0.4) !important'
                }
              },
              '& .MuiTab-root.Mui-selected': {
                backgroundColor: 'rgba(96, 165, 250, 0.3) !important',
                border: '2px solid rgba(96, 165, 250, 0.5) !important',
                color: 'var(--text-link) !important',
                fontWeight: 700
              }
            }}
          >
            <Tab label="Basic Information" />
            <Tab label="Security" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {modalTab === 0 && (
              <Grid container spacing={3}>
                {/* Vendor Selection - Only for Admins creating new stewards */}
                {isAdmin && !editingSteward && (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      required
                      error={!!fieldErrors.vendorId}
                    >
                      <InputLabel>Select Vendor</InputLabel>
                      <Select
                        name="vendorId"
                        value={formData.vendorId}
                        onChange={(e) => {
                          setFormData({ ...formData, vendorId: e.target.value as string });
                          setFieldErrors({ ...fieldErrors, vendorId: '' });
                        }}
                        label="Select Vendor"
                        disabled={loadingVendors}
                      >
                        {loadingVendors ? (
                          <MenuItem value="">
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Loading vendors...
                          </MenuItem>
                        ) : vendors.length === 0 ? (
                          <MenuItem value="" disabled>
                            No vendors available
                          </MenuItem>
                        ) : (
                          vendors.map((vendor) => (
                            <MenuItem key={vendor.id} value={vendor.id.toString()}>
                              {vendor.businessName || vendor.username} (ID: {vendor.id})
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {fieldErrors.vendorId && (
                        <Typography variant="caption" sx={{ color: 'var(--status-bad)', mt: 0.5, ml: 1.75 }}>
                          {fieldErrors.vendorId}
                        </Typography>
                      )}
                      {!fieldErrors.vendorId && (
                        <Typography variant="caption" sx={{ color: theme.gray?.[600], mt: 0.5, ml: 1.75 }}>
                          Select which vendor this steward will work for
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <TextField
                    name="name"
                    label="Full Name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      setFieldErrors({ ...fieldErrors, name: '' });
                    }}
                    required
                    fullWidth
                    error={!!fieldErrors.name}
                    helperText={fieldErrors.name || ' '}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    name="email"
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      setFieldErrors({ ...fieldErrors, email: '' });
                    }}
                    required
                    fullWidth
                    error={!!fieldErrors.email}
                    helperText={fieldErrors.email || ' '}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    name="stewardCode"
                    label="Steward Code"
                    value={formData.stewardCode}
                    onChange={(e) => {
                      setFormData({ ...formData, stewardCode: e.target.value });
                      setFieldErrors({ ...fieldErrors, stewardCode: '' });
                    }}
                    required
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IdentificationIcon className="h-5 w-5" style={{ color: theme.gray?.[600] }} />
                        </InputAdornment>
                      ),
                    }}
                    error={!!fieldErrors.stewardCode}
                    helperText={fieldErrors.stewardCode || 'Unique code for steward login'}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ pt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          sx={{
                            color: 'rgba(96, 165, 250, 0.5)',
                            '&.Mui-checked': {
                              color: 'var(--text-link) !important',
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(96, 165, 250, 0.1)',
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                          Active Status
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>

                {/* Finance-only restriction — only meaningful on creation; vendors
                    cannot toggle it on existing rows from this form today. */}
                <Grid item xs={12}>
                  <Box sx={{ pt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="financeOnlyRestricted"
                          inputProps={{ 'aria-label': 'Restrict to finance entry only' }}
                          checked={Boolean(formData.financeOnlyRestricted)}
                          onChange={(e) =>
                            setFormData({ ...formData, financeOnlyRestricted: e.target.checked })
                          }
                          disabled={Boolean(editingSteward)}
                          sx={{
                            color: 'color-mix(in srgb, var(--action) 50%, transparent)',
                            '&.Mui-checked': { color: `${theme.primary || 'var(--action)'} !important` },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                            Restrict to finance entry only (Add Income / Expense / EOD)
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                            Steward lands on Income / Expense / EOD only,
                            sees just their own entries, and cannot edit or
                            delete anything. Vendor retains full override.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                </Grid>
              </Grid>
            )}

            {modalTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {editingSteward && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Leave password fields empty to keep the current password
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      setFieldErrors({ ...fieldErrors, password: '' });
                    }}
                    required={!editingSteward}
                    fullWidth
                    error={!!fieldErrors.password}
                    helperText={fieldErrors.password || ' '}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <KeyIcon className="h-5 w-5" style={{ color: theme.gray?.[600] }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <AppIconButton
                            icon={showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                            tooltip={showPassword ? "Hide password" : "Show password"}
                            variant="primary"
                            onClick={() => setShowPassword(v => !v)}
                            edge="end"
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    name="confirmPassword"
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => {
                      setFormData({ ...formData, confirmPassword: e.target.value });
                      setFieldErrors({ ...fieldErrors, confirmPassword: '' });
                    }}
                    required={!editingSteward}
                    fullWidth
                    error={!!fieldErrors.confirmPassword}
                    helperText={fieldErrors.confirmPassword || ' '}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <AppIconButton
                            icon={showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                            tooltip={showConfirmPassword ? "Hide password" : "Show password"}
                            variant="primary"
                            onClick={() => setShowConfirmPassword(v => !v)}
                            edge="end"
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'var(--text-link)' }}>
                      Security Requirements
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.gray?.[700], lineHeight: 1.6 }}>
                      • Password must be at least 6 characters long<br />
                      • Include a mix of letters and numbers for better security<br />
                      • Avoid using common words or patterns
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                onClick={handleCloseModal}
                variant="outlined"
                sx={{
                  textTransform: 'none !important',
                  fontWeight: '600 !important',
                  px: 3,
                  py: 1,
                  backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
                  borderColor: 'rgba(96, 165, 250, 0.31) !important',
                  color: 'var(--text-link) !important',
                  borderRadius: '8px',
                  boxShadow: 'none !important',
                  '&:hover': {
                    backgroundColor: 'rgba(96, 165, 250, 0.3) !important',
                    borderColor: 'rgba(96, 165, 250, 0.5) !important',
                    boxShadow: 'none !important',
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  textTransform: 'none !important',
                  fontWeight: '600 !important',
                  px: 3,
                  py: 1,
                  backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
                  color: 'var(--text-link) !important',
                  border: '1px solid rgba(96, 165, 250, 0.31) !important',
                  borderRadius: '8px',
                  boxShadow: 'none !important',
                  '&:hover': {
                    backgroundColor: 'rgba(96, 165, 250, 0.3) !important',
                    borderColor: 'rgba(96, 165, 250, 0.5) !important',
                    boxShadow: 'none !important',
                  }
                }}
              >
                {editingSteward ? 'Update' : 'Create'} Steward
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Table Assignment Modal */}
      <Modal
        isOpen={isAssignmentModalOpen}
        onClose={() => setIsAssignmentModalOpen(false)}
        title={`Assign Tables to ${selectedSteward?.name}`}
        maxWidth="md"
      >
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Select the tables this steward will be responsible for
          </Alert>

          <Box sx={{ maxHeight: 400, overflow: 'auto', border: `1px solid ${theme.components?.card?.border}`, borderRadius: 1 }}>
            {tables.map((table) => (
              <Box
                key={table.id}
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${theme.components?.card?.border}`,
                  '&:hover': { backgroundColor: theme.components?.card?.hover }
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTables.includes(table.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTables([...selectedTables, table.id]);
                        } else {
                          setSelectedTables(selectedTables.filter(id => id !== table.id));
                        }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: theme.text }}>
                        {table.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.gray?.[600] }}>
                        {table.section} • {table.floor}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => setIsAssignmentModalOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSaveAssignments}
              variant="contained"
              sx={{
                backgroundColor: '#60A5FA !important', /* basil-todo */
                color: 'var(--text-inverse) !important',
                boxShadow: '0 1px 2px rgba(96, 165, 250, 0.25) !important',
                '&:hover': {
                  backgroundColor: '#3B9EF6 !important', /* basil-todo */
                  boxShadow: '0 2px 4px rgba(96, 165, 250, 0.35) !important',
                }
              }}
            >
              Save Assignments
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default StewardManagement;